import React, { useEffect, useMemo, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import Pagination from '@cloudscape-design/components/pagination';
import Tabs from '@cloudscape-design/components/tabs';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import type { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import { getSupplierInsightsService } from '../services';
import type {
    InsightsOverview,
    InsightsSupplier,
    InsightsTransaction,
    InsightsPurchaseOrder,
    InsightsPoLine,
    InsightsOutcome,
} from '../services/types';

// Supplier Insights — internal reporting on supplier performance. Ported from
// the dscs prototypes (Overview + ASN Review + Supplier Review + PO Review +
// PO Lines Review), wired to the SupplierInsightsService. Data is empty/zeroed
// until the backend is built.

type InsightsView = 'dashboard' | 'transactions' | 'suppliers' | 'pos' | 'polines';

// ── Shared date-range control ─────────────────────────────────────────────────
type Preset = '7d' | '30d' | '90d';
const PRESET_OPTIONS = [
    { id: '7d', text: '7d' },
    { id: '30d', text: '30d' },
    { id: '90d', text: '90d' },
];

function DateRange({ preset, onChange }: { preset: Preset; onChange: (p: Preset) => void }) {
    return (
        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Box color="text-body-secondary">Date range:</Box>
            <SegmentedControl
                selectedId={preset}
                onChange={({ detail }) => onChange(detail.selectedId as Preset)}
                options={PRESET_OPTIONS}
            />
        </SpaceBetween>
    );
}

// ── CSV export helper ─────────────────────────────────────────────────────────
function downloadCsv(filename: string, rows: (string | number)[][]) {
    const esc = (v: string | number) => {
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const content = rows.map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

const PAGE_SIZE = 15;

/** Paginate a filtered list for a 1-based current page. */
function paginate<T>(items: T[], page: number): T[] {
    return items.slice((page - 1) * PAGE_SIZE, (page - 1) * PAGE_SIZE + PAGE_SIZE);
}

// ── Overview dashboard ────────────────────────────────────────────────────────
function OverviewView({ overview }: { overview: InsightsOverview }) {
    const [preset, setPreset] = useState<Preset>('30d');

    const kpis = [
        {
            label: 'Total ASNs',
            value: overview.asnCount ? overview.asnCount.toLocaleString() : '—',
            sub: 'shipments notified',
        },
        { label: 'Suppliers', value: overview.supplierCount || '—', sub: 'active this period' },
        {
            label: 'On-time %',
            value: overview.onTimePct ? `${overview.onTimePct}%` : '—',
            sub: 'against promised dates',
        },
        {
            label: 'Error rate',
            value: overview.errorRate ? `${overview.errorRate}%` : '—',
            sub: 'data + business errors',
        },
    ];

    return (
        <SpaceBetween size="l">
            <DateRange preset={preset} onChange={setPreset} />
            <ColumnLayout columns={4}>
                {kpis.map((k) => (
                    <Container key={k.label}>
                        <SpaceBetween size="xxxs">
                            <Box color="text-body-secondary" fontSize="body-s">
                                {k.label}
                            </Box>
                            <Box fontSize="display-l" fontWeight="bold">
                                {String(k.value)}
                            </Box>
                            <Box color="text-body-secondary" fontSize="body-s">
                                {k.sub}
                            </Box>
                        </SpaceBetween>
                    </Container>
                ))}
            </ColumnLayout>
            <ColumnLayout columns={2}>
                <Container header={<Header variant="h3">ASN volume trend</Header>}>
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        No data to chart yet.
                    </Box>
                </Container>
                <Container header={<Header variant="h3">Supplier adoption</Header>}>
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        No data to chart yet.
                    </Box>
                </Container>
            </ColumnLayout>
        </SpaceBetween>
    );
}

// ── ASN Review (all transactions) ─────────────────────────────────────────────
type Bucket = 'passed' | 'data' | 'business';
const BUCKET_FILTERS: { id: Bucket; label: string }[] = [
    { id: 'passed', label: 'Passed' },
    { id: 'data', label: 'Supplier data errors' },
    { id: 'business', label: 'Business errors' },
];
function bucketOf(o: InsightsOutcome): Bucket {
    if (o === 'passed') return 'passed';
    if (o === 'business') return 'business';
    return 'data';
}
const OUTCOME_LABEL: Record<InsightsOutcome, string> = {
    passed: 'Passed',
    schema: 'Supplier data error',
    semantic: 'Supplier data error',
    business: 'Business error',
};
const OUTCOME_STATUS: Record<InsightsOutcome, StatusIndicatorProps.Type> = {
    passed: 'success',
    schema: 'warning',
    semantic: 'warning',
    business: 'error',
};

function TransactionsView({ rows }: { rows: InsightsTransaction[] }) {
    const [preset, setPreset] = useState<Preset>('30d');
    const [selected, setSelected] = useState<Set<Bucket>>(new Set());
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);

    const toggle = (b: Bucket) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(b)) next.delete(b);
            else next.add(b);
            return next;
        });
        setPage(1);
    };

    const query = q.trim().toLowerCase();
    const filtered = rows.filter((t) => {
        if (selected.size > 0 && !selected.has(bucketOf(t.outcome))) return false;
        if (
            query &&
            !t.asn.toLowerCase().includes(query) &&
            !t.supplier.toLowerCase().includes(query) &&
            !t.pos.some((po) => po.toLowerCase().includes(query))
        )
            return false;
        return true;
    });
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageRows = paginate(filtered, Math.min(page, pageCount));

    const exportCsv = () =>
        downloadCsv('all-transactions.csv', [
            ['ASN', 'Supplier', 'POs', 'Lines', 'Received', 'Outcome', 'Error'],
            ...filtered.map((t) => [
                t.asn,
                t.supplier,
                t.pos.join(' | '),
                t.lines,
                t.date,
                OUTCOME_LABEL[t.outcome],
                t.error,
            ]),
        ]);

    return (
        <Table
            variant="container"
            items={pageRows}
            header={
                <Header
                    actions={
                        <Button iconName="download" onClick={exportCsv}>
                            Export
                        </Button>
                    }
                >
                    All ASNs
                </Header>
            }
            filter={
                <SpaceBetween size="s">
                    <DateRange preset={preset} onChange={setPreset} />
                    <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                        <Box color="text-body-secondary">Outcome:</Box>
                        {BUCKET_FILTERS.map((o) => (
                            <Button
                                key={o.id}
                                variant={selected.has(o.id) ? 'primary' : 'normal'}
                                onClick={() => toggle(o.id)}
                            >
                                {o.label}
                            </Button>
                        ))}
                    </SpaceBetween>
                    <TextFilter
                        filteringText={q}
                        filteringPlaceholder="Search ASN, PO, supplier…"
                        onChange={({ detail }) => {
                            setQ(detail.filteringText);
                            setPage(1);
                        }}
                    />
                </SpaceBetween>
            }
            pagination={
                <Pagination
                    currentPageIndex={Math.min(page, pageCount)}
                    pagesCount={pageCount}
                    onChange={({ detail }) => setPage(detail.currentPageIndex)}
                />
            }
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No ASN transactions to show yet.
                </Box>
            }
            columnDefinitions={[
                { id: 'asn', header: 'ASN', cell: (t) => t.asn },
                { id: 'supplier', header: 'Supplier', cell: (t) => t.supplier },
                {
                    id: 'pos',
                    header: 'POs',
                    cell: (t) =>
                        t.pos.length > 1 ? `${t.pos[0]} +${t.pos.length - 1}` : t.pos[0] ?? '—',
                },
                { id: 'received', header: 'Received', cell: (t) => t.date },
                {
                    id: 'outcome',
                    header: 'Outcome',
                    cell: (t) => (
                        <StatusIndicator type={OUTCOME_STATUS[t.outcome]}>
                            {OUTCOME_LABEL[t.outcome]}
                        </StatusIndicator>
                    ),
                },
                { id: 'detail', header: 'Detail', cell: (t) => t.error || '—' },
                { id: 'attempts', header: 'Attempts', cell: (t) => t.attempts || '—' },
            ]}
        />
    );
}

// ── Supplier Review (directory) ───────────────────────────────────────────────
function SuppliersView({ rows }: { rows: InsightsSupplier[] }) {
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const query = q.trim().toLowerCase();
    const filtered = rows.filter(
        (s) =>
            query === '' ||
            s.name.toLowerCase().includes(query) ||
            s.id.toLowerCase().includes(query),
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageRows = paginate(filtered, Math.min(page, pageCount));

    const complianceColor = (v: number): StatusIndicatorProps.Type => {
        if (v >= 95) return 'success';
        if (v >= 93) return 'warning';
        return 'error';
    };

    return (
        <Table
            variant="container"
            items={pageRows}
            header={<Header>Suppliers</Header>}
            filter={
                <TextFilter
                    filteringText={q}
                    filteringPlaceholder="Search supplier or vendor ID…"
                    onChange={({ detail }) => {
                        setQ(detail.filteringText);
                        setPage(1);
                    }}
                />
            }
            pagination={
                <Pagination
                    currentPageIndex={Math.min(page, pageCount)}
                    pagesCount={pageCount}
                    onChange={({ detail }) => setPage(detail.currentPageIndex)}
                />
            }
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No suppliers to show yet.
                </Box>
            }
            columnDefinitions={[
                {
                    id: 'supplier',
                    header: 'Supplier',
                    cell: (s) => (
                        <SpaceBetween size="xxxs">
                            <Box fontWeight="bold">{s.name}</Box>
                            <Box color="text-body-secondary" fontSize="body-s">
                                {s.id}
                            </Box>
                        </SpaceBetween>
                    ),
                },
                { id: 'asns', header: 'ASNs', cell: (s) => s.asns.toLocaleString() },
                {
                    id: 'compliance',
                    header: 'Compliance',
                    cell: (s) =>
                        s.compliance ? (
                            <StatusIndicator type={complianceColor(s.compliance)}>
                                {`${s.compliance.toFixed(1)}%`}
                            </StatusIndicator>
                        ) : (
                            '—'
                        ),
                },
                {
                    id: 'valid',
                    header: 'Validation %',
                    cell: (s) => (s.valid ? `${s.valid.toFixed(1)}%` : '—'),
                },
                {
                    id: 'dsOk',
                    header: 'Downstream %',
                    cell: (s) => (s.dsOk ? `${s.dsOk.toFixed(1)}%` : '—'),
                },
                { id: 'last', header: 'Last ASN', cell: (s) => s.last },
                {
                    id: 'status',
                    header: 'Status',
                    cell: (s) => (
                        <StatusIndicator type={s.status === 'Active' ? 'success' : 'warning'}>
                            {s.status}
                        </StatusIndicator>
                    ),
                },
            ]}
        />
    );
}

// ── Purchase Orders Review ────────────────────────────────────────────────────
function PurchaseOrdersView({ rows }: { rows: InsightsPurchaseOrder[] }) {
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const query = q.trim().toLowerCase();
    const filtered = rows.filter(
        (p) =>
            query === '' ||
            p.po.toLowerCase().includes(query) ||
            p.supplier.toLowerCase().includes(query),
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageRows = paginate(filtered, Math.min(page, pageCount));

    return (
        <Table
            variant="container"
            items={pageRows}
            header={<Header>Purchase orders</Header>}
            filter={
                <TextFilter
                    filteringText={q}
                    filteringPlaceholder="Search PO or supplier…"
                    onChange={({ detail }) => {
                        setQ(detail.filteringText);
                        setPage(1);
                    }}
                />
            }
            pagination={
                <Pagination
                    currentPageIndex={Math.min(page, pageCount)}
                    pagesCount={pageCount}
                    onChange={({ detail }) => setPage(detail.currentPageIndex)}
                />
            }
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No purchase orders to show yet.
                </Box>
            }
            columnDefinitions={[
                { id: 'po', header: 'PO', cell: (p) => p.po },
                { id: 'supplier', header: 'Supplier', cell: (p) => p.supplier },
                { id: 'lines', header: 'Lines', cell: (p) => p.lines },
                { id: 'needBy', header: 'Need-by', cell: (p) => p.needBy },
                { id: 'status', header: 'Status', cell: (p) => p.status },
            ]}
        />
    );
}

// ── PO Lines Review ───────────────────────────────────────────────────────────
function PoLinesView({ rows }: { rows: InsightsPoLine[] }) {
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const query = q.trim().toLowerCase();
    const filtered = rows.filter(
        (l) =>
            query === '' ||
            l.po.toLowerCase().includes(query) ||
            l.item.toLowerCase().includes(query) ||
            l.supplier.toLowerCase().includes(query),
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageRows = paginate(filtered, Math.min(page, pageCount));

    return (
        <Table
            variant="container"
            items={pageRows}
            header={<Header>PO lines</Header>}
            filter={
                <TextFilter
                    filteringText={q}
                    filteringPlaceholder="Search PO, item, supplier…"
                    onChange={({ detail }) => {
                        setQ(detail.filteringText);
                        setPage(1);
                    }}
                />
            }
            pagination={
                <Pagination
                    currentPageIndex={Math.min(page, pageCount)}
                    pagesCount={pageCount}
                    onChange={({ detail }) => setPage(detail.currentPageIndex)}
                />
            }
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No PO lines to show yet.
                </Box>
            }
            columnDefinitions={[
                { id: 'po', header: 'PO', cell: (l) => l.po },
                { id: 'line', header: 'Line', cell: (l) => l.line },
                { id: 'item', header: 'Item', cell: (l) => l.item },
                { id: 'supplier', header: 'Supplier', cell: (l) => l.supplier },
                { id: 'ordered', header: 'Ordered', cell: (l) => l.ordered },
                { id: 'received', header: 'Received', cell: (l) => l.received },
                { id: 'needBy', header: 'Need-by', cell: (l) => l.needBy },
            ]}
        />
    );
}

// ── Main Insights area ────────────────────────────────────────────────────────
const EMPTY_OVERVIEW: InsightsOverview = {
    supplierCount: 0,
    asnCount: 0,
    onTimePct: 0,
    errorRate: 0,
};

export default function SupplierInsightsScreen() {
    const [view, setView] = useState<InsightsView>('dashboard');
    const [overview, setOverview] = useState<InsightsOverview>(EMPTY_OVERVIEW);
    const [suppliers, setSuppliers] = useState<InsightsSupplier[]>([]);
    const [transactions, setTransactions] = useState<InsightsTransaction[]>([]);
    const [pos, setPos] = useState<InsightsPurchaseOrder[]>([]);
    const [poLines, setPoLines] = useState<InsightsPoLine[]>([]);

    useEffect(() => {
        let live = true;
        const svc = getSupplierInsightsService();
        Promise.all([
            svc.getOverview(),
            svc.listSuppliers(),
            svc.listTransactions(),
            svc.listPurchaseOrders(),
            svc.listPoLines(),
        ])
            .then(([o, s, t, p, l]) => {
                if (!live) return;
                setOverview(o);
                setSuppliers(s);
                setTransactions(t);
                setPos(p);
                setPoLines(l);
            })
            .catch(() => {
                if (!live) return;
                setOverview(EMPTY_OVERVIEW);
                setSuppliers([]);
                setTransactions([]);
                setPos([]);
                setPoLines([]);
            });
        return () => {
            live = false;
        };
    }, []);

    const tabs = useMemo(
        () => [
            { id: 'dashboard', label: 'Overview', content: <OverviewView overview={overview} /> },
            {
                id: 'transactions',
                label: 'ASN Review',
                content: <TransactionsView rows={transactions} />,
            },
            {
                id: 'suppliers',
                label: 'Supplier Review',
                content: <SuppliersView rows={suppliers} />,
            },
            {
                id: 'pos',
                label: 'Purchase Orders Review',
                content: <PurchaseOrdersView rows={pos} />,
            },
            { id: 'polines', label: 'PO Lines Review', content: <PoLinesView rows={poLines} /> },
        ],
        [overview, transactions, suppliers, pos, poLines],
    );

    return (
        <Tabs
            activeTabId={view}
            onChange={({ detail }) => setView(detail.activeTabId as InsightsView)}
            tabs={tabs}
        />
    );
}
